import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import Seo, { faqSchema } from "@/components/Seo";
import Marquee from "@/components/Marquee";
import MapEmbed from "@/components/MapEmbed";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { BookButton, SectionHeading } from "@/components/ui-bits";
import { HOTEL, IMAGES, ROOMS, AMENITIES, REVIEWS, ATTRACTIONS, FAQS } from "@/lib/site";

const easing = [0.16, 1, 0.3, 1];
const heroLines = ["A warm welcome", "in the heart of", "Pittsburg, Kansas"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroFacade} alt="Guest room at Regency Inn & Suites in Pittsburg, Kansas" className="w-full h-full object-cover brightness-[0.62] saturate-[1.05]" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/55 to-forest-deep/70" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 20%, transparent 30%, rgba(13,28,23,0.75) 100%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-xs uppercase tracking-[0.35em] text-amber mb-6"
        >
          Independent · Pet Friendly · Since {HOTEL.established}
        </motion.span>

        <h1 className="font-serif text-bone text-[13vw] sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          {heroLines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.13, duration: 1, ease: easing }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: easing }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <BookButton label="Book Direct & Save" testid="hero-book-btn" />
          <Link
            to="/rooms"
            data-testid="hero-rooms-link"
            className="link-underline text-bone text-sm uppercase tracking-widest"
          >
            Explore Our Rooms →
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 hidden md:flex items-center gap-3 text-bone/70 text-xs uppercase tracking-widest"
      >
        <span>Scroll</span>
        <span className="h-10 w-px bg-bone/40" />
      </motion.div>
    </section>
  );
}

const CHAPTERS = [
  { n: "01", title: "The Welcome", copy: "A locally owned inn where the front desk knows your name and every detail is handled with heartland hospitality." },
  { n: "02", title: "The Rest", copy: "Clean, quiet, comfortable rooms with plush beds, 50-inch LED TVs and calm interiors made for a genuine night's sleep." },
  { n: "03", title: "The Table", copy: "A complimentary hot breakfast each morning and a microwave and refrigerator in every room — value you can taste." },
  { n: "04", title: "The Long Stay", copy: "Weekly and monthly rates, kitchenettes, laundry and truck parking built for travelers who stay a while." },
];

function Manifesto() {
  return (
    <section className="bg-bone py-28 lg:py-40" data-testid="home-manifesto">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <SectionHeading eyebrow="Why Stay Here" title="Four reasons guests keep coming back." />
        </Reveal>
        <div className="mt-20 grid md:grid-cols-2 gap-x-16 gap-y-16">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="flex gap-8 border-t border-stone/20 pt-8">
                <span className="font-serif text-5xl text-amber/70 leading-none">{c.n}</span>
                <div>
                  <h3 className="font-serif text-3xl text-forest mb-3">{c.title}</h3>
                  <p className="text-stone leading-relaxed">{c.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoomsPreview() {
  return (
    <section className="bg-sand py-28 lg:py-36" data-testid="home-rooms">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <Reveal>
            <SectionHeading eyebrow="Our Rooms" title="Space to rest, work & stay a while." />
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/rooms" className="link-underline text-sm uppercase tracking-widest text-forest" data-testid="home-all-rooms">
              View All Rooms →
            </Link>
          </Reveal>
        </div>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ROOMS.map((r) => (
            <StaggerItem key={r.slug}>
              <Link to={`/rooms/${r.slug}`} data-testid={`home-room-${r.slug}`} className="group block">
                <div className="img-zoom aspect-[3/4] rounded-md">
                  <img src={r.images[0]} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-widest text-amber-deep">{r.beds}</p>
                  <h3 className="font-serif text-2xl text-forest mt-1 group-hover:text-amber-deep transition-colors">{r.name}</h3>
                  <p className="text-sm text-stone mt-1">{r.short}</p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function AmenitiesStrip() {
  return (
    <section className="bg-forest text-bone py-28 lg:py-36" data-testid="home-amenities">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-3 gap-14">
          <div>
            <Reveal>
              <SectionHeading eyebrow="Everything Included" title="Comforts that make the difference." light />
              <p className="mt-6 text-bone/75 leading-relaxed">
                Free WiFi, free breakfast, pet-friendly rooms, in-room kitchenettes and generous free parking — the
                amenities you expect from a higher-priced hotel, at an honest, independent rate.
              </p>
              <Link
                to="/amenities"
                className="mt-8 inline-block link-underline text-amber text-sm uppercase tracking-widest"
                data-testid="home-all-amenities"
              >
                See All Amenities →
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-2">
            <Stagger className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10">
              {AMENITIES.slice(0, 9).map((a) => {
                const Icon = Icons[a.icon] || Icons.Check;
                return (
                  <StaggerItem key={a.title}>
                    <Icon className="text-amber" size={26} strokeWidth={1.4} />
                    <h4 className="font-serif text-xl mt-3">{a.title}</h4>
                    <p className="text-sm text-bone/65 mt-2 leading-relaxed">{a.copy}</p>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}

function AttractionsPreview() {
  return (
    <section className="bg-bone py-28 lg:py-36" data-testid="home-attractions">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <SectionHeading eyebrow="The Neighborhood" title="Minutes from everything that matters." />
        </Reveal>
        <Stagger className="mt-16 grid md:grid-cols-3 gap-6">
          {ATTRACTIONS.slice(0, 6).map((a) => (
            <StaggerItem key={a.name}>
              <div className="group border border-stone/15 rounded-md p-7 hover:border-amber transition-colors h-full">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs uppercase tracking-widest text-amber-deep">{a.cat}</span>
                  <span className="text-xs text-stone">{a.dist}</span>
                </div>
                <h3 className="font-serif text-2xl text-forest">{a.name}</h3>
                <p className="text-sm text-stone mt-2 leading-relaxed">{a.copy}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <div className="mt-10 text-center">
            <Link to="/attractions" className="link-underline text-sm uppercase tracking-widest text-forest" data-testid="home-all-attractions">
              Explore Pittsburg →
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section className="bg-sand py-28 lg:py-36" data-testid="home-reviews">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex text-amber">
              {[...Array(5)].map((_, i) => (
                <Icons.Star key={i} size={20} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <span className="text-sm text-stone">{HOTEL.rating} · {HOTEL.reviewCount}+ guest reviews</span>
          </div>
          <SectionHeading eyebrow="Guest Stories" title="Loved by families, teams & travelers." />
        </Reveal>
        <Stagger className="mt-16 grid md:grid-cols-2 gap-8">
          {REVIEWS.map((r) => (
            <StaggerItem key={r.name}>
              <figure className="bg-bone rounded-md p-8 border border-stone/10 h-full">
                <div className="flex text-amber mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Icons.Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="font-serif text-2xl text-forest leading-snug mb-5">“{r.title}”</blockquote>
                <p className="text-stone leading-relaxed text-[15px]">{r.text}</p>
                <figcaption className="mt-5 text-sm uppercase tracking-widest text-amber-deep">— {r.name}</figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function FaqPreview() {
  return (
    <section className="bg-bone py-28 lg:py-36" data-testid="home-faq">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <SectionHeading eyebrow="Good to Know" title="Answers to your top questions." />
          <p className="mt-6 text-stone leading-relaxed">
            From check-in times and breakfast to pets, parking and extended-stay rates — here are quick answers to what
            guests ask most.
          </p>
          <Link to="/faq" className="mt-8 inline-block link-underline text-sm uppercase tracking-widest text-forest" data-testid="home-all-faq">
            View All FAQs →
          </Link>
        </Reveal>
        <div>
          {FAQS.slice(0, 5).map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="border-t border-stone/20 py-6">
                <h3 className="font-serif text-xl text-forest">{f.q}</h3>
                <p className="text-stone mt-2 leading-relaxed text-[15px]">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="relative overflow-hidden" data-testid="home-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionHeading eyebrow="Reserve Your Stay" title="Your room in Pittsburg is waiting." />
          <p className="mt-6 text-stone leading-relaxed max-w-md">
            Book directly for our best available rate — no OTA fees, just a warm welcome. Questions? Call our 24-hour
            front desk anytime.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <BookButton label="Book Direct & Save" testid="cta-book" />
            <a
              href={HOTEL.phoneHref}
              data-testid="cta-call"
              className="inline-flex items-center justify-center rounded-full border border-forest text-forest px-8 py-4 text-sm font-medium hover:bg-forest hover:text-bone transition-colors"
            >
              Call {HOTEL.phone}
            </a>
          </div>
        </div>
        <MapEmbed height="380px" />
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Regency Inn & Suites | Best Hotel in Pittsburg, Kansas"
        description="Regency Inn & Suites is a clean, affordable, pet-friendly hotel in Pittsburg, KS. Free WiFi, free breakfast, extended-stay rates & truck parking near Pittsburg State University. Book direct & save."
        path="/"
        schema={[faqSchema(FAQS.slice(0, 10))]}
      />
      <Hero />
      <div className="bg-forest-deep py-8">
        <Marquee items={["Free Breakfast", "Pet Friendly", "Free WiFi", "Extended Stay", "Truck Parking", "Near PSU"]} />
      </div>
      <Manifesto />
      <RoomsPreview />
      <AmenitiesStrip />
      <AttractionsPreview />
      <Reviews />
      <FaqPreview />
      <ClosingCta />
    </>
  );
}
